import React, { useEffect, useState } from "react";
import { adminListAudit } from "../../lib/api";

/**
 * AuditSection — admin moderation activity log. Reads /api/admin/audit
 * and renders the most recent 100 entries with filter chips for
 * action type + target type.
 */
export function AuditSection({ onToast }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [targetType, setTargetType] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const params = { limit: 100 };
      if (targetType) params.target_type = targetType;
      const r = await adminListAudit(params);
      setRows(r.rows || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load audit log.");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [targetType]);

  const filters = [
    { id: "",          label: "ALL" },
    { id: "user",      label: "USERS" },
    { id: "league",    label: "LEAGUES" },
    { id: "challenge", label: "MATCHES" },
    { id: "grant",     label: "GRANTS" },
    { id: "season_pass", label: "SEASON PASS" },
  ];

  return (
    <div data-testid="admin-audit-section">
      <div className="flex flex-wrap gap-1 mb-4" data-testid="admin-audit-filters">
        {filters.map((f) => (
          <button
            key={f.id || "all"}
            type="button"
            onClick={() => setTargetType(f.id)}
            data-testid={`admin-audit-filter-${f.id || "all"}`}
            className={`text-[11px] tracking-widest px-3 py-1.5 rounded-xl ${
              targetType === f.id ? "btn-neon" : "btn-ghost"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {busy ? (
        <p className="text-[11px] tracking-widest text-[#14b8a6] animate-pulse">LOADING…</p>
      ) : rows.length === 0 ? (
        <p className="text-[11px] text-white/45" data-testid="admin-audit-empty">
          No audit entries yet for this filter.
        </p>
      ) : (
        <div className="space-y-2" data-testid="admin-audit-list">
          {rows.map((r) => (
            <AuditRow key={r.audit_id} row={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function AuditRow({ row }) {
  const t = row.created_at ? new Date(row.created_at) : null;
  const color = ACTION_COLOR[row.action?.split("_")[0]] || "text-white/70";
  const [open, setOpen] = useState(false);
  const hasDiff = row.before || row.after || (row.metadata && Object.keys(row.metadata).length);
  return (
    <div data-testid={`admin-audit-row-${row.audit_id}`}
         className="glass rounded-xl p-3 text-[12px]">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className={`font-bold ${color} text-[10px] tracking-widest`}>
            {row.action?.toUpperCase().replace(/_/g, " ")}
          </p>
          <p className="text-white/85 truncate">{row.summary || "—"}</p>
          <p className="text-white/40 text-[10px] mt-0.5">
            {row.admin_email || "—"} · {t ? t.toLocaleString() : "—"}
            {row.target_type && <> · {row.target_type}</>}
          </p>
        </div>
        {hasDiff && (
          <button type="button" onClick={() => setOpen(!open)}
                  data-testid={`admin-audit-toggle-${row.audit_id}`}
                  className="text-[10px] tracking-widest px-2 py-1 rounded-md bg-white/5 border border-white/10 hover:bg-white/10">
            {open ? "HIDE" : "DIFF"}
          </button>
        )}
      </div>
      {open && (
        <pre data-testid={`admin-audit-diff-${row.audit_id}`}
             className="mt-2 p-2 rounded-lg bg-black/60 border border-white/10 text-[10px] text-[#a3a3ac] overflow-auto max-h-60">
{JSON.stringify({ before: row.before, after: row.after, metadata: row.metadata }, null, 2)}
        </pre>
      )}
    </div>
  );
}

const ACTION_COLOR = {
  report: "text-[#14b8a6]",
  dispute: "text-[#14b8a6]",
  match: "text-[#14b8a6]",
  dissolve: "text-[#14b8a6]",
  transfer: "text-[#ffe066]",
  grant: "text-[#14b8a6]",
  season: "text-[#14b8a6]",
};
